import { Box } from "@mui/material";
import { VisualGroupContext } from "../VisualGroupHeading";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { useThemeContext } from "../../globalContext/global-context";
import type { SurveyAttempt } from "../../session/SurveySession";
import type { Question } from "../../util/types";
import type {
  AnyQuestionModule,
  HostControl,
  HostControlBindings,
  QuestionViewProps,
} from "../contracts";
import { resolveQuestionView } from "../display";
import { useSpecifyControl } from "../specify/useSpecifyControl";
import type { AttemptDraftState, SessionQuestionSlotProps } from "./types";
import {
  createDraftState,
  readDraftValue,
  writeDraftValue,
  writeSpecifyDraft,
  rejectedResponseVersion,
} from "./util/draftState";

const supportsControls = (
  available: readonly HostControl[],
  required: readonly HostControl[],
) => required.every((control) => available.includes(control));

type ActiveQuestionProps = SessionQuestionSlotProps & {
  attempt: SurveyAttempt;
  module: AnyQuestionModule;
  raw?: Question["question"];
  draftId?: string;
  draftScope?: string;
  answered?: boolean;
  deferSubmission?: boolean;
  deferredCanSubmit?: boolean;
  deferredAvailableAtMs?: number;
  onDeferredSubmit?: () => void;
  correctionActive?: boolean;
  groupContext?: string;
};

export const ActiveQuestion = ({
  attempt,
  session,
  Frame,
  arrangement,
  preferredResponseFlow,
  capabilities,
  view,
  promptInHistory = false,
  history,
  draftState: sharedDraftState,
  setDraftState: setSharedDraftState,
  onInspect,
  module,
  raw: suppliedRaw,
  draftId = "question",
  draftScope = `attempt:${attempt.id}`,
  answered = false,
  deferSubmission = false,
  deferredCanSubmit = true,
  deferredAvailableAtMs,
  onDeferredSubmit,
  correctionActive = true,
  groupContext,
}: ActiveQuestionProps) => {
  const { globalObject } = useThemeContext();
  const copy = globalObject.themeData.normalizedTheme.copy;
  const snapshot = useSyncExternalStore(session.subscribe, session.getSnapshot);
  const raw = suppliedRaw ?? attempt.question.question;
  const definition = useMemo(() => module.core.decode(raw), [module, raw]);
  const initialDraft = useMemo(
    () => module.core.initialize(definition),
    [definition, module],
  );
  const restoredAttempt = rejectedResponseVersion(raw, attempt.id);
  const [localDraftState, setLocalDraftState] =
    useState<AttemptDraftState | null>(() =>
      createDraftState(
        session,
        draftScope,
        draftId,
        initialDraft,
        restoredAttempt,
      ),
    );
  const draftState = sharedDraftState ?? localDraftState;
  const setDraftState = setSharedDraftState ?? setLocalDraftState;
  const draft = readDraftValue(
    draftState,
    session,
    draftScope,
    draftId,
    initialDraft,
    restoredAttempt,
  );
  const canSubmit = snapshot.canSubmit && deferredCanSubmit;
  const viewId = resolveQuestionView(
    module,
    raw,
    arrangement,
    view,
    preferredResponseFlow,
  );
  const descriptor = module.views[viewId];
  if (!descriptor) throw new Error(`Unknown ${module.type} view '${viewId}'`);
  if (
    !descriptor.arrangements.includes(arrangement) ||
    !supportsControls(capabilities.controls, descriptor.requiredControls)
  ) {
    throw new Error(
      `Unsupported ${module.type} view '${viewId}' for ${arrangement}`,
    );
  }

  const display = { view: viewId, arrangement };
  const requested = module.controls(definition, draft, display, copy);
  const validation = useMemo(
    () => module.core.validate(definition, draft, copy),
    [definition, draft, module, copy],
  );
  useEffect(() => {
    onInspect?.({
      definition,
      draft,
      validation,
      ...(validation.valid
        ? { transcript: module.core.projectTranscript(definition, draft, copy) }
        : {}),
    });
  }, [definition, draft, module, onInspect, validation, copy]);

  if (
    requested.textInput?.multiline &&
    !capabilities.inputModes.includes("multiline")
  ) {
    throw new Error(`Host does not support multiline input for ${module.type}`);
  }

  const dispatch = (event: unknown) => {
    if (answered) return;
    setDraftState((current) => {
      const currentDraft = readDraftValue(
        current,
        session,
        draftScope,
        draftId,
        draft,
        restoredAttempt,
      );
      return writeDraftValue(
        current,
        session,
        draftScope,
        draftId,
        module.core.update(definition, currentDraft, event),
        restoredAttempt,
      );
    });
  };
  const submitDraft = (candidate: unknown) => {
    if (!canSubmit || !module.core.validate(definition, candidate, copy).valid)
      return;
    session.submit(
      attempt,
      module.core.encode(definition, candidate),
      module.core.projectTranscript(definition, candidate, copy),
    );
  };
  const submit = (event?: unknown) => {
    /* v8 ignore next -- answered group content is inert */
    if (answered) return;
    const candidate =
      event === undefined
        ? draft
        : module.core.update(definition, draft, event);
    if (event !== undefined) {
      setDraftState((current) =>
        writeDraftValue(
          current,
          session,
          draftScope,
          draftId,
          candidate,
          restoredAttempt,
        ),
      );
    }
    if (deferSubmission) {
      onDeferredSubmit?.();
      return;
    }
    submitDraft(candidate);
  };
  const runPrimaryAction = () => {
    const action = module.controls(
      definition,
      draft,
      display,
      copy,
    ).primaryAction;
    if (!action || action.disabled || !canSubmit || answered) return;
    if (action.action.type === "update") dispatch(action.action.event);
    else if (!deferSubmission) submitDraft(draft);
  };
  const specify = useSpecifyControl({
    attemptId: attempt.id,
    active: correctionActive && !answered,
    error: module.prompt(definition).error,
    fields: requested.specify ?? [],
    canSubmit: canSubmit && !answered,
    retainedValues:
      draftState?.session === session && draftState.scopeKey === draftScope
        ? (draftState.specifyDrafts?.[draftId] ?? {})
        : {},
    retain: (key, value) =>
      setDraftState((current) =>
        writeSpecifyDraft(current, session, draftScope, draftId, key, value),
      ),
    apply: (event, shouldSubmit) => {
      const candidate = module.core.update(definition, draft, event);
      setDraftState((current) =>
        writeDraftValue(
          current,
          session,
          draftScope,
          draftId,
          candidate,
          restoredAttempt,
        ),
      );
      if (shouldSubmit && !deferSubmission) submitDraft(candidate);
    },
  });
  const controls: HostControlBindings = {
    canSubmit: canSubmit && !answered,
    ...(requested.specify?.length ? { specify: specify.control } : {}),
    ...(requested.primaryAction
      ? {
          primaryAction: {
            purpose: requested.primaryAction.purpose,
            label: requested.primaryAction.label,
            disabled:
              Boolean(requested.primaryAction.disabled) ||
              !canSubmit ||
              answered ||
              (requested.primaryAction.purpose === "submit" &&
                !validation.valid),
            pending: Boolean(requested.primaryAction.pending),
            run: runPrimaryAction,
          },
        }
      : {}),
    ...(requested.secondaryAction
      ? {
          secondaryAction: {
            label: requested.secondaryAction.label,
            selected: requested.secondaryAction.selected,
            disabled:
              Boolean(requested.secondaryAction.disabled) ||
              !canSubmit ||
              answered,
            run: () =>
              requested.secondaryAction!.selected && !deferSubmission
                ? submitDraft(draft)
                : submit(requested.secondaryAction!.action.event),
          },
        }
      : {}),
    ...(requested.textInput
      ? {
          textInput: {
            fieldId: requested.textInput.fieldId,
            label: requested.textInput.label,
            value: requested.textInput.value,
            hint: requested.textInput.hint,
            error:
              !validation.valid &&
              (!validation.fieldId ||
                validation.fieldId === requested.textInput.fieldId)
                ? validation.error
                : undefined,
            multiline: Boolean(requested.textInput.multiline),
            inputMode: requested.textInput.inputMode,
            prefix: requested.textInput.prefix,
            suffix: requested.textInput.suffix,
            disabled: answered || !snapshot.canSubmit,
            onChange: (value: string) =>
              dispatch(requested.textInput!.event(value)),
            onConfirm: runPrimaryAction,
          },
        }
      : {}),
  };
  const presentationControls: HostControlBindings =
    answered && deferSubmission
      ? {
          ...controls,
          canSubmit: true,
          /* v8 ignore next -- only text questions expose text input bindings */
          ...(controls.textInput
            ? {
                textInput: {
                  ...controls.textInput,
                  disabled: false,
                },
              }
            : {}),
        }
      : controls;
  const View = descriptor.component;
  const suggestedActionRef = useRef<(() => void) | null>(null);
  const registerSuggestedAction = useCallback((action: (() => void) | null) => {
    suggestedActionRef.current = action;
  }, []);
  const runSuggestedAction = useCallback(() => {
    if (!suggestedActionRef.current) return false;
    suggestedActionRef.current();
    return true;
  }, []);
  const viewProps: QuestionViewProps<unknown, unknown, unknown> = {
    definition,
    active: correctionActive,
    availableAtMs: deferredAvailableAtMs ?? attempt.availableAtMs,
    draft,
    dispatch,
    submit,
    sampleDial: (value, offsetSeconds, postData) =>
      session.sampleDial(attempt, value, offsetSeconds, postData),
    completeVideo: (value, offsetSeconds, postData) =>
      session.completeVideo(attempt, value, offsetSeconds, postData),
    controls: presentationControls,
    registerSuggestedAction,
    display,
  };

  return (
    <Box
      data-testid="registered-question"
      data-attempt-id={attempt.id}
      sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}
    >
      <Frame
        attemptId={attempt.id}
        answered={answered}
        submittable={validation.valid || raw.auto_response === "ok"}
        busy={snapshot.pending !== null}
        arrangement={arrangement}
        controls={presentationControls}
        prompt={module.prompt(definition)}
        promptInHistory={promptInHistory}
        history={history}
        primaryActionOwner={descriptor.primaryActionOwner ?? "host"}
        input={descriptor.input ?? "none"}
        space={
          module.resolveSpace?.(definition) ?? descriptor.space ?? "standard"
        }
        showChoiceHint={requested.showChoiceHint}
        autoResponse={
          raw.auto_response
            ? {
                disabled: !snapshot.canSubmit,
                run: () => session.submitAutoResponse(attempt),
              }
            : undefined
        }
        autoSubmit={
          requested.autoSubmitReady === undefined
            ? undefined
            : {
                ready:
                  requested.autoSubmitReady &&
                  snapshot.canSubmit &&
                  validation.valid,
                run: () => submitDraft(draft),
              }
        }
        runSuggestedAction={runSuggestedAction}
        choiceHint={requested.choiceHint ?? "multichoice"}
        inputHint={requested.inputHint}
        response={
          <>
            <VisualGroupContext question={raw} parentGroup={groupContext} />
            <View key={`${attempt.id}-${draftId}`} {...viewProps} />
          </>
        }
      />
      {specify.editor}
    </Box>
  );
};
